import React, { useState } from "react";
import { Dialog, Zoom, IconButton } from "@mui/material";
import { getImageUrl } from "../../Helpers/Helpers";

const FIELD_META = {
  price: { label: "Prix", icon: "fa-tag", suffix: " TND" },
  remise: { label: "Remise", icon: "fa-percent", suffix: "%" },
  quantity: { label: "Quantité", icon: "fa-cubes", suffix: "" },
};

// Full-detail, single-item editor shown when a cart row is long-pressed.
// Price/remise/quantity are all bound to the same on-screen Calculator
// keypad used elsewhere in the cart — tap a field to make it the active
// target, then type on the Calculator.
const FocusedCartItem = ({
  item,
  editingField,
  fieldBuffer,
  onStartEditField,
  onStopEditField,
  onBack,
  onRemove,
}) => {
  const [imageExpanded, setImageExpanded] = useState(false);

  if (!item) return null;

  const isWeight = item.productInfo?.unite && item.productInfo.unite !== "pcs";
  const unite = isWeight ? item.productInfo.unite : "";

  const displayValue = (field) => {
    if (editingField === field) {
      return fieldBuffer || (field === "quantity" ? "1" : "0");
    }
    if (field === "quantity") return `${item.quantity}${unite}`;
    if (field === "price") return item.price;
    return item.remise || 0;
  };

  const renderField = (field) => {
    const meta = FIELD_META[field];
    const active = editingField === field;
    return (
      <div
        key={field}
        role="button"
        onClick={() => (active ? onStopEditField() : onStartEditField(field))}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderRadius: 14,
          marginBottom: 10,
          cursor: "pointer",
          border: active ? "2px solid #fd683e" : "2px solid #f0f0f0",
          backgroundColor: active ? "#fff3ec" : "#fafafa",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: active ? "scale(1.02)" : "scale(1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: active ? "#fd683e" : "#eee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.25s ease",
              flexShrink: 0,
            }}
          >
            <i
              className={`fa ${meta.icon}`}
              style={{ color: active ? "#fff" : "#888", fontSize: 14 }}
            />
          </div>
          <span style={{ fontWeight: 600, color: "#555" }}>{meta.label}</span>
        </div>
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: active ? "#fd683e" : "#222",
          }}
        >
          {displayValue(field)}
          {field !== "quantity" ? meta.suffix : ""}
        </span>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          animation: "focusedItemIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <style>{`
          @keyframes focusedItemIn {
            from { opacity: 0; transform: translateY(8px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <IconButton size="small" onClick={onBack}>
            <i className="fa fa-arrow-left" />
          </IconButton>
          <h5 className="m-0" style={{ fontWeight: 700 }}>
            Détails du produit
          </h5>
        </div>

        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* Photo — tap to expand */}
          <div
            role="button"
            onClick={() => setImageExpanded(true)}
            style={{
              width: 110,
              height: 110,
              borderRadius: 16,
              overflow: "hidden",
              flexShrink: 0,
              cursor: "zoom-in",
              boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
              transition: "transform 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.03)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "scale(1)")
            }
          >
            <img
              src={getImageUrl(item.productInfo.image)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "images/product/no-image.png";
              }}
              alt={item.productInfo.titre?.fr}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h4
              className="m-0"
              style={{ fontWeight: 700, overflowWrap: "break-word" }}
            >
              {item.productInfo.titre?.fr}
            </h4>
            <div
              style={{
                marginTop: 8,
                fontSize: 22,
                fontWeight: 800,
                color: "#fd683e",
              }}
            >
              {item.netPrice?.toFixed(3)} TND
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          {renderField("price")}
          {renderField("remise")}
          {renderField("quantity")}
        </div>

        <button
          className="btn btn-outline-danger mt-auto"
          onClick={onRemove}
          style={{ borderRadius: 10, fontWeight: 600 }}
        >
          <i className="ti-trash mr-2" />
          Retirer du panier
        </button>
      </div>

      {/* Photo lightbox */}
      <Dialog
        open={imageExpanded}
        onClose={() => setImageExpanded(false)}
        TransitionComponent={Zoom}
        maxWidth="xs"
      >
        <img
          src={getImageUrl(item.productInfo.image)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "images/product/no-image.png";
          }}
          alt={item.productInfo.titre?.fr}
          style={{ width: "100%", display: "block", cursor: "zoom-out" }}
          onClick={() => setImageExpanded(false)}
        />
      </Dialog>
    </>
  );
};

export default FocusedCartItem;
